import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { scopeText } from '@/lib/scope';
import { FinePlacementPanel } from '@/pages/anatel/FinePlacementPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { fetchEntity } from '@/services/anatel';

export function AnatelEntityProfilePage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { cnpj = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'entity', cnpj],
    queryFn: () => fetchEntity(cnpj),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('anatel:entity.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const number = new Intl.NumberFormat(i18n.language);
  const scope = scopeText(data.as_of, t);

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{data.canonical_name}</h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span className="tabular-nums">{data.cnpj}</span>
          {data.cnpj_valid ? (
            <span className="inline-flex items-center gap-1 text-xs">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
              {t('anatel:process.cnpjValid')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {t('anatel:entities.cnpjInvalid')}
            </span>
          )}
        </p>
      </header>

      <ExplainerCard i18nKey="anatel:entity.explainer" />

      <FieldGuide i18nKey="anatel:entity.guide" level={2} />

      {/* Its own strip, not `AsOfStrip`: the span beside the scope is THIS
          COMPANY's first and last decision, not the corpus window, and folding
          it into the shared component would let a reader take one company's
          activity for the coverage of the whole corpus. The scope sentence is
          still translated through the shared hook. */}
      <div
        data-as-of
        className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
      >
        <span className="font-medium">{t('common:asOf.scope')}:</span> {scope}
        {data.earliest && data.latest ? (
          <>
            {' · '}
            {data.earliest} — {data.latest}
          </>
        ) : null}
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label={t('anatel:overview.corpus.processes')} value={number.format(data.processes)} />
        <Stat
          label={t('anatel:overview.corpus.finalDecisions')}
          value={number.format(data.final_decisions)}
        />
        <div className="rounded-lg border bg-card p-3">
          <dt className="text-xs text-muted-foreground">
            {t('anatel:entity.seeAllDecisions')}
          </dt>
          <dd className="mt-1">
            {/* Drill-through: the same list screen, pre-filtered to this company.
                One definition of "this company's decisions", not two. */}
            <Link
              to={`/anatel/decisions?q=${encodeURIComponent(data.canonical_name)}`}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              {t('anatel:entity.openDecisions')}
            </Link>
          </dd>
        </div>
      </dl>

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel answer={data.outcome_mix} />
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('anatel:entity.citations.title')}
          </h2>
          {data.top_citations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('anatel:entity.citations.none')}</p>
          ) : (
            <ul className="space-y-1.5">
              {data.top_citations.map((row) => (
                <li key={row.citation} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-foreground">{row.citation}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {number.format(row.count)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <FinePlacementPanel answer={data.fine_placement} />
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['anatel']);
  return (
    <Link
      to="/anatel/entities"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('anatel:entity.backToList')}
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}
